import { createContext, useContext, useState, ReactNode } from 'react';
import { Language, Translations } from '@/i18n/types';
import { translations } from '@/i18n/translations';
import { logger } from '@/services/logger';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: Translations;
  rehydrateFromApi: (apiLanguage: Language | null) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGE_STORAGE_KEY = 'datamission_language';

/**
 * Provider component that manages language state and translations
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    // Try to load language from localStorage
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored && (stored === 'en' || stored === 'fr' || stored === 'de')) {
        return stored as Language;
      }
    } catch (err) {
      logger.warn('Failed to load language preference', {
        context: 'LanguageContext.initialize',
        error: err instanceof Error ? err.message : String(err),
      });
    }

    // Default to English
    return 'en';
  });

  /**
   * Update language and persist to localStorage
   */
  const setLanguage = (newLanguage: Language) => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
      setLanguageState(newLanguage);
      logger.info('Language changed', {
        context: 'LanguageContext.setLanguage',
        language: newLanguage,
      });
    } catch (err) {
      logger.error(err instanceof Error ? err : new Error('Failed to save language preference'), {
        context: 'LanguageContext.setLanguage',
      });
    }
  };

  /**
   * Rehydrate language from API response, updating local state if it differs
   */
  const rehydrateFromApi = (apiLanguage: Language | null) => {
    if (apiLanguage && apiLanguage !== language) {
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, apiLanguage);
        setLanguageState(apiLanguage);
        logger.info('Language rehydrated from API', {
          context: 'LanguageContext.rehydrateFromApi',
          language: apiLanguage,
        });
      } catch (err) {
        logger.error(err instanceof Error ? err : new Error('Failed to rehydrate language'), {
          context: 'LanguageContext.rehydrateFromApi',
        });
      }
    }
  };

  // Get current translations based on selected language
  const t = translations[language];

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        t,
        rehydrateFromApi,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to access language context
 * @throws Error if used outside of LanguageProvider
 */
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    // eslint-disable-next-line no-restricted-syntax
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
